/* ==========================================================================
   Blenders Online 2026 · bo-footer.js · manners for bo-newsletter and bo-footer
   (the water, the dial and PULSE themselves are the effects kit's: bo-fx.js)

   1. Footer columns (<details open data-bo-foot-col>): open and fixed on tablets and computers,
      accordions on phones (closed until tapped). Without this script every column is simply open.
   2. The big footer logo plays its entrance the first time it is scrolled to (not unseen at page
      load), and its idle liquid rests while it is off screen.
   3. The little blender in the dial fills with the speed, and runs (hum, lid, vortex) at 10 and on PULSE.
   4. The newsletter form: a wrong address is caught in place (aria-invalid + a message read out),
      the button shows it is busy while the form is sent; after a sign-up the thank-you is focused.
   Idempotent; re-binds on shopify:section:load. Nothing runs under reduced motion that moves.
   ========================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.BOFooter) { W.BOFooter.init(D); return; }
  const phone = W.matchMedia('(max-width: 749px)');
  const reduce = W.matchMedia('(prefers-reduced-motion: reduce)');
  const each = (root, sel, fn) => { if (root.matches && root.matches(sel)) fn(root); root.querySelectorAll(sel).forEach(fn); };

  /* ---------- 1. columns ---------- */
  const syncCols = () => D.querySelectorAll('[data-bo-foot-col]').forEach((d) => {
    const s = d.querySelector('summary');
    if (!s) return;
    if (phone.matches) {
      if (!d.boTouched) d.open = false;
      s.removeAttribute('tabindex');
    } else {
      d.open = true;
      s.setAttribute('tabindex', '-1');         // nothing to toggle on a computer: no empty tab stop
    }
  });
  const bindCol = (d) => {
    if (d.boBound) return;
    d.boBound = 1;
    const s = d.querySelector('summary');
    if (!s) return;
    s.addEventListener('click', (e) => { if (!phone.matches) e.preventDefault(); else d.boTouched = 1; });
  };
  if (phone.addEventListener) phone.addEventListener('change', syncCols);

  /* ---------- 2. the logo: entrance when first seen, idle only on screen ---------- */
  const seenIO = 'IntersectionObserver' in W ? new IntersectionObserver((ens) => ens.forEach((en) => {
    const svg = en.target, a = svg.closest('.bo-foot__logo');
    if (svg.classList.contains('bo-logo--idle') || svg.boIdle) svg.classList.toggle('bo-logo--paused', !en.isIntersecting);
    if (en.isIntersecting && svg.boWaiting) {
      svg.boWaiting = 0;
      svg.classList.add('bo-logo--animate', 'bo-logo--idle');
      if (a) a.classList.remove('is-waiting');
    }
  }), { threshold: 0.35 }) : null;
  const bindLogo = (svg) => {
    if (svg.boBound || !seenIO) return;
    svg.boBound = 1;
    svg.boIdle = 1;
    const a = svg.closest('.bo-foot__logo');
    const r = svg.getBoundingClientRect();
    const onScreen = r.top < W.innerHeight && r.bottom > 0;
    const skip = reduce.matches || D.documentElement.classList.contains('bo-logo-seen') || onScreen;
    svg.classList.remove('bo-logo--inview');
    if (!skip) {                                  // hold the entrance (and the idle that follows it) until it is seen
      svg.classList.remove('bo-logo--animate', 'bo-logo--idle');
      svg.boWaiting = 1;
      if (a) a.classList.add('is-waiting');
    }
    seenIO.observe(svg);
  };
  const idleIO = 'IntersectionObserver' in W ? new IntersectionObserver((ens) => ens.forEach((en) => en.target.classList.toggle('bo-logo--paused', !en.isIntersecting))) : null;

  /* ---------- 3. the dial's blender follows the speed: its level, and at 10 or PULSE it runs ---------- */
  D.addEventListener('bo:fx:speed', (e) => {
    const foot = e.target.closest && e.target.closest('[data-bo-foot]');
    if (!foot || !e.detail) return;
    // the jug fills with the speed (Tommy 01-10): speed 1 sits about half full, 10 (and PULSE) fills it to the brim
    const jug = foot.querySelector('.bo-foot__dialjug');
    if (jug) {
      const sp = e.detail.pulsing ? 10 : Math.min(10, Math.max(1, +e.detail.speed || 1));
      jug.style.setProperty('--bo-jug-drop', (50 - (sp - 1) * 50 / 9).toFixed(1) + '%');
    }
    // the vortex builds with the speed: 8 a gentle swirl, 9 a stronger one, 10 the full run (motor hum, lid, light,
    // vortex), PULSE the full run quicker; 7 and under the surface only drifts (Tommy 01-10)
    const pulsing = !!e.detail.pulsing, sp2 = +e.detail.speed || 1;
    const tier = reduce.matches ? 0 : pulsing || sp2 >= 9.5 ? 3 : sp2 >= 8.5 ? 2 : sp2 >= 7.5 ? 1 : 0;
    foot.querySelectorAll('.bo-foot__dialjug .bo-logo').forEach((svg) => {
      svg.classList.toggle('bo-logo--running', tier === 3);
      svg.classList.toggle('bo-logo--whirl-2', tier === 2);
      svg.classList.toggle('bo-logo--whirl-1', tier === 1);
      svg.classList.toggle('bo-logo--burst', tier === 3 && pulsing);
    });
    foot.boPulsing = pulsing;
  });

  /* ---------- 4. the newsletter form ---------- */
  const bindForm = (form) => {
    if (form.boBound) return;
    form.boBound = 1;
    const input = form.querySelector('input[type="email"]');
    const btn = form.querySelector('button[type="submit"]');
    const box = form.querySelector('[data-bo-news-error]');
    if (!input || !box) return;
    const say = (text) => {
      box.textContent = '';
      if (!text) { input.removeAttribute('aria-invalid'); return; }
      const icon = D.createElementNS('http://www.w3.org/2000/svg', 'svg');
      icon.setAttribute('class', 'bo-icon');
      icon.setAttribute('viewBox', '0 0 24 24');
      icon.setAttribute('aria-hidden', 'true');
      icon.innerHTML = '<circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" stroke-width="2"/><path d="M12 7.5v5.5M12 16.5h.01" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round"/>';
      const span = D.createElement('span');
      span.textContent = text;
      box.append(icon, span);
      input.setAttribute('aria-invalid', 'true');
    };
    form.addEventListener('submit', (e) => {
      input.value = input.value.trim();
      if (!input.value || !input.checkValidity()) {
        e.preventDefault();
        say(box.dataset.boMsg || 'Please enter a valid email address.');
        input.focus();
        return;
      }
      say('');
      if (btn) { btn.classList.add('is-loading'); btn.setAttribute('aria-busy', 'true'); }
    });
    input.addEventListener('input', () => { if (input.getAttribute('aria-invalid') && input.checkValidity() && input.value.trim()) say(''); });
  };
  const focusThanks = () => {
    const ok = D.querySelector('[data-bo-news-ok]');
    if (!ok || !/[?&]customer_posted=true/.test(W.location.search)) return;
    try { ok.focus({ preventScroll: false }); } catch (e) { ok.focus(); }
    // welcome to the Vortex: the footer's water gives one long pulse
    if (reduce.matches) return;
    setTimeout(() => {
      const water = D.querySelector('.bo-foot [data-bo-fx="water"]');
      const fx = water && W.BOFx && W.BOFx.get(water);
      if (fx && fx.pulse) fx.pulse(1600);
    }, 900);
  };
  W.addEventListener('pageshow', () => D.querySelectorAll('[data-bo-news-form] .is-loading').forEach((b) => { b.classList.remove('is-loading'); b.removeAttribute('aria-busy'); }));

  /* ---------- 5. the blender's water rises behind "Stay in the Vortex" (Tommy 01-10) ----------
     When the newsletter band stands right on top of the footer, the footer's water reaches up behind it: the band's
     height goes into --bo-news-h (kept in step as it changes), the band is marked so it lets the water show through
     and stays on top of it (bo-footer.css). The water re-measures itself when its element grows. */
  const riseWater = (foot) => {
    if (foot.boRisen) return;
    const water = foot.querySelector(':scope > [data-bo-fx="water"]');
    const sec = foot.closest('.shopify-section');
    const prev = sec && sec.previousElementSibling;
    if (!water || !prev || !prev.querySelector('.bo-news')) return;
    foot.boRisen = 1;
    prev.classList.add('bo-news-over-water');
    foot.classList.add('bo-foot--risen');
    const set = () => foot.style.setProperty('--bo-news-h', Math.round(prev.getBoundingClientRect().height) + 'px');
    set();
    if ('ResizeObserver' in W) new ResizeObserver(set).observe(prev);
  };

  /* ---------- boot ---------- */
  const api = W.BOFooter = {
    init(root) {
      root = root || D;
      each(root, '[data-bo-foot-col]', bindCol);
      syncCols();
      each(root, '.bo-foot__logo .bo-logo', bindLogo);
      if (idleIO) each(root, '.bo-foot__dialjug .bo-logo', (svg) => { if (!svg.boIdleBound) { svg.boIdleBound = 1; idleIO.observe(svg); } });
      each(root, '[data-bo-news-form]', bindForm);
      each(root, '[data-bo-foot]', riseWater);
    }
  };
  const boot = () => { api.init(D); focusThanks(); };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
  D.addEventListener('shopify:section:load', (e) => api.init(e.target));
})();
